import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { work } from "@/data/work";
import { site } from "@/config/site";
import { Section } from "./Section";
import { Reveal, ImageReveal, EASE } from "./Reveal";

const Lightbox = ({ index, onClose, onNav }) => {
    const item = work[index];
    const onKey = useCallback(
        (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") onNav(1);
            if (e.key === "ArrowLeft") onNav(-1);
        },
        [onClose, onNav],
    );
    useEffect(() => {
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onKey]);

    return createPortal(
        <motion.div
            data-testid="work-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={item.title}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-rz-black/95 p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
        >
            <button
                data-testid="lightbox-close"
                onClick={onClose}
                aria-label="Close image viewer"
                className="absolute right-5 top-5 border border-rz-steel p-2 text-rz-white transition-colors hover:border-rz-red"
            >
                <X size={20} />
            </button>
            <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); onNav(-1); }}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 border border-rz-steel p-2 text-rz-white transition-colors hover:border-rz-red sm:left-6"
            >
                <ChevronLeft size={20} />
            </button>
            <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); onNav(1); }}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 border border-rz-steel p-2 text-rz-white transition-colors hover:border-rz-red sm:right-6"
            >
                <ChevronRight size={20} />
            </button>
            <figure className="max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                <motion.img
                    key={item.id}
                    src={item.image}
                    alt={item.alt}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="max-h-[78vh] w-auto max-w-full object-contain"
                />
                <figcaption className="mt-4 flex items-center gap-4">
                    <span className="font-display text-[11px] uppercase tracking-[0.22em] text-rz-red">
                        {item.category}
                    </span>
                    <span className="text-sm text-rz-smoke">{item.title}</span>
                </figcaption>
            </figure>
        </motion.div>,
        document.body,
    );
};

const Tile = ({ w, i, onOpen, featured = false }) => (
    <button
        data-testid={`work-tile-${w.id}`}
        onClick={() => onOpen(i)}
        className={`rz-corners group relative block w-full overflow-hidden border border-rz-steel/60 text-left ${
            featured ? "h-full" : ""
        }`}
        aria-label={`View ${w.title}`}
    >
        <ImageReveal className="h-full">
            <img
                src={w.image}
                alt={w.alt}
                loading="lazy"
                decoding="async"
                className={`${featured ? "h-full min-h-[320px]" : w.aspect} w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
            />
        </ImageReveal>
        <div className="absolute inset-0 bg-gradient-to-t from-rz-black/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        <div className="absolute bottom-0 left-0 w-full translate-y-2 p-5 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
            <span className="block h-px w-10 bg-rz-red" />
            <span className="mt-3 block font-display text-[11px] uppercase tracking-[0.22em] text-rz-white">
                {w.category}
            </span>
            <span className="mt-1 block text-sm text-rz-smoke">{w.title}</span>
        </div>
        {featured && (
            <span className="absolute left-5 top-5 border border-rz-red/60 bg-rz-black/60 px-3 py-1 font-display text-[9px] uppercase tracking-[0.25em] text-rz-red backdrop-blur-sm">
                Featured
            </span>
        )}
    </button>
);

export const Work = () => {
    const [open, setOpen] = useState(null);
    const nav = (dir) => setOpen((o) => (o + dir + work.length) % work.length);
    const [featured, ...rest] = work;

    return (
        <Section id="work" index="12" label="Our Work" title="Real cars. Real transformations." alt>
            <div className="mt-14 grid gap-6 lg:grid-cols-12">
                <Reveal className="lg:col-span-7" delay={0.05}>
                    <Tile w={featured} i={0} onOpen={setOpen} featured />
                </Reveal>
                <div className="grid grid-cols-2 gap-6 lg:col-span-5">
                    {rest.slice(0, 4).map((w, j) => (
                        <Reveal key={w.id} delay={0.08 + j * 0.05}>
                            <Tile w={w} i={j + 1} onOpen={setOpen} />
                        </Reveal>
                    ))}
                </div>
            </div>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
                {rest.slice(4).map((w, j) => (
                    <Reveal key={w.id} delay={j * 0.05}>
                        <Tile w={w} i={j + 5} onOpen={setOpen} />
                    </Reveal>
                ))}
            </div>
            <Reveal className="mt-10 flex flex-wrap items-center justify-between gap-4" delay={0.1}>
                <p className="text-[12px] uppercase tracking-[0.18em] text-rz-smoke">
                    Real Revautoz project photography will replace these placeholders
                </p>
                <a
                    data-testid="work-instagram-cta"
                    href={site.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-display text-[12px] font-semibold uppercase tracking-[0.2em] text-rz-red transition-transform duration-300 hover:translate-x-1"
                >
                    View all work on Instagram {site.instagramHandle} →
                </a>
            </Reveal>
            <AnimatePresence>
                {open !== null && <Lightbox index={open} onClose={() => setOpen(null)} onNav={nav} />}
            </AnimatePresence>
        </Section>
    );
};
